import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { ISize } from '@/version-types';
import { DrawPenDataType } from '../../constants';
import { EliminateType, IPathInfo } from '../../types';

interface IDrawPenPathProps {
    drawingPath: IPathInfo | null;
    drawPathList: Array<IPathInfo>;
    disable?: boolean;
    watermarkMaskURL?: string;
    textMaskURL?: string;
}

// TODO: @cxz1 跟组件合并成同一份
/**
 * 这里是用于消除算法实际使用的 MASK
 */
export const getDrawPenSVG = (
    size: ISize,
    pathInfos: Array<IPathInfo>,
    type: EliminateType,
    watermarkMaskURL?: string,
    textMaskURL?: string,
    fillColor?: string,
) => {
    // ATTENTION: merge 算法，需要用 127 来表示，算法会用来做 mask 膨胀处理
    const maskFillColor =
        fillColor || (type === EliminateType.AI ? 'rgba(127, 127, 127, 255)' : 'rgba(255, 255, 255, 255)');
    const maskId = uuid();

    // ATTENTION: SVG 的声明必须是第一行且没有缩进和空格，否则会 SVG 解析失败
    // ATTENTION: 这里需要使用 viewBox 来指定 SVG 的尺寸，否则会导致低端浏览器绘制到 Canvas 空白
    return `<?xml version="1.0" standalone="no"?><!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
        <svg xmlns="http://www.w3.org/2000/svg"
            version="1.1"
            width="100%"
            height="100%"
            viewBox="0 0 ${size.width} ${size.height}"
        >
            <rect width="100%" height="100%" fill="${maskFillColor}" mask="url(#mask_${maskId})" />
            <mask id="mask_${maskId}">
                ${watermarkMaskURL ? `<image href="${watermarkMaskURL}" width="100%" height="100%"></image>` : ''}
                ${textMaskURL ? `<image href="${textMaskURL}" width="100%" height="100%"></image>` : ''}
                ${pathInfos
                    .map(({ strokeWidth, d, stroke, fill, strokeLineCap }) => {
                        return `
                            <path
                                d="${d}"
                                stroke="${stroke || 'none'}"
                                stroke-width="${strokeWidth || '0'}"
                                fill="${fill || 'none'}"
                                stroke-linecap="${strokeLineCap || ''}"
                                stroke-linejoin="round"
                            ></path>
                        `;
                    })
                    .join('')}
            </mask>
        </svg>
    `;
};

// ATTENTION: 修复 React 组件的逻辑，需要同步到 getDrawPenSVG
/**
 * 这里是用于 UI 上的画笔显示
 */
export const DrawPenPath: React.FC<IDrawPenPathProps> = ({
    drawingPath,
    drawPathList,
    disable = false,
    watermarkMaskURL,
    textMaskURL,
}) => {
    const maskId = useMemo(() => {
        return `mask_${uuid()}`;
    }, []);

    return (
        <svg data-type={DrawPenDataType} width="100%" height="100%" style={{ pointerEvents: disable ? 'none' : 'all' }}>
            <rect width="100%" height="100%" fill="rgba(108, 142, 229, 0.6)" mask={`url(#mask_${maskId})`} />
            {drawingPath && (
                <path
                    style={{ pointerEvents: 'none' }}
                    d={drawingPath.d}
                    stroke={drawingPath.stroke || 'none'}
                    strokeWidth={drawingPath.strokeWidth || '0'}
                    fill={drawingPath.fill || 'none'}
                    strokeLinecap={drawingPath.strokeLineCap}
                    strokeLinejoin="round"
                ></path>
            )}
            <mask id={`mask_${maskId}`}>
                {watermarkMaskURL && <image href={watermarkMaskURL} width="100%" height="100%"></image>}
                {textMaskURL && <image href={textMaskURL} width="100%" height="100%"></image>}
                {/* attention: 兼容Firefox， Firefox可能没有正确刷新 mask 的渲染结果, 但实际的dom节点已经消失 */}
                {drawPathList.length === 0 ? (
                    <rect x="0" y="0" width="0" height="0" fill="white" />
                ) : (
                    drawPathList.map(({ strokeWidth, d, stroke, fill, strokeLineCap }, index) => (
                        <path
                            style={{ pointerEvents: 'none' }}
                            key={index}
                            d={d}
                            stroke={stroke || 'none'}
                            strokeWidth={strokeWidth || '0'}
                            fill={fill || 'none'}
                            strokeLinecap={strokeLineCap}
                            strokeLinejoin="round"
                        />
                    ))
                )}
            </mask>
        </svg>
    );
};
